import { Trans } from '@lingui/react/macro';
import { Button } from '@warp-ds/react';
import { Controller, useFormContext } from 'react-hook-form';
import type { ValueOption, YesNoWidgetModel } from '../../../types/widget.js';

interface YesNoWidgetProps extends Omit<YesNoWidgetModel, 'type'> {
  values: Array<ValueOption>;
}

const sortYesFirst = (values: YesNoWidgetProps['values']) => {
  return values?.sort((a, b) => {
    const aIsYes = a.code === 'YES';
    const bIsYes = b.code === 'YES';

    if (aIsYes && !bIsYes) return -1;
    if (!aIsYes && bIsYes) return 1;
    return 0;
  });
};

export const YesNoWidget = (props: YesNoWidgetProps) => {
  const { control, resetField } = useFormContext();

  return (
    <Controller
      name={props.name}
      control={control}
      render={({ field }) => {
        const helpTextHintReference = `${props.name}_hint`;

        const buttons = sortYesFirst(props.values)?.map((value) => {
          const isActiveButton = field.value !== undefined && (field.value === value.id || field.value === value.code);

          return (
            <Button
              className={`min-w-full`}
              onClick={() =>
                isActiveButton ? resetField(props.name, { defaultValue: undefined }) : field.onChange(value.id)
              }
              data-value={value.id}
              key={value.id}
              secondary={!isActiveButton}
              primary={isActiveButton}
              small
            >
              {value.label}
            </Button>
          );
        });

        const optionalLabel = props.mandatory ? (
          ''
        ) : (
          <span className="font-normal text-s s-text-subtle">
            (
            <Trans
              id="mobility-item-insertion.optional.label"
              comment="Used in a input field label to show that the field is optional"
            >
              optional
            </Trans>
            )
          </span>
        );

        return (
          <div className={props.className}>
            <fieldset
              className="w-full md:w-2/5"
              data-attribute={props.name}
              data-value={field.value}
              data-testid={props.name}
            >
              <legend className="text-s font-bold pb-8">
                {props.label} {optionalLabel}
              </legend>
              <div className="grid grid-cols-2 gap-16" aria-describedby={helpTextHintReference}>
                {buttons}
              </div>
              <p id={helpTextHintReference} className="text-xs s-text-subtle mt-8">
                {props.helpText}
              </p>
            </fieldset>
          </div>
        );
      }}
    />
  );
};
